import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import CalendarEventItem from "./calendar-event-item";
import {
  computeEventColumnLayout,
  formatHourLabel,
  formatSubHourLabel,
  isSameDay,
  isToday,
  toDateKey,
} from "./calendar-utils";
import {
  WEEK_GUTTER_REM,
  weekAnchorScrollLeft,
  weekColumnMetrics,
  weekGridWidthStyle,
} from "./week-day-window";
import { getRootFontSizePx } from "./calendar-drag-geometry";
import { applyZoomAnchor, centreZoomAnchor } from "./calendar-zoom-anchor";
import { eventGridPositionForAxis, nowIndicatorForAxis } from "./calendar-time-axis";
import { fitSpanMinutes } from "./calendar-fit-zoom";
import { BlockChip, BlockOverlay, blocksForDate } from "./calendar-blocks";

/**
 * Full-height drop surface for one week day. Also carries the `data-time-col`
 * marker the drag geometry measures — the div spans the h-7 header spacer plus
 * the whole time grid, matching measureColumnGeometry's expectations.
 * columnId stays null: a cross-day drop changes the date, never the owner.
 */
function WeekDayColumnDroppable({ dayKey, isPadding = false, className, children }) {
  const droppableId = `col:${dayKey}:day`;
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: { type: "column", dayKey, columnId: null, groupBy: null },
  });
  return (
    <div
      ref={setNodeRef}
      data-time-col={droppableId}
      // Days borrowed from the neighbouring weeks are knocked back so the anchor
      // week still reads as "the week you are looking at".
      className={cn(className, isPadding && "bg-muted/15", isOver && "bg-primary/5")}
    >
      {children}
    </div>
  );
}

function WeekView({
  weekDays,
  dragPlaceholder = null,
  eventsByDate,
  selectedDate,
  onSelectDate,
  use12HourFormat = false,
  zoomPercent = 100,
  widthZoomPercent = 100,
  /** Fixed day run from getWeekDayWindow — zoom-independent (see week-day-window.js). */
  dayWindow,
  timeAxis,
  initialScrollToNow = true,
  initialScrollRatio = null,
  onSaveScrollRatio,
  onDidInitialScroll,
  registerViewport,
  zoomAnchorRef,
  resetSignal = 0,
  timeOffBlocks,
  doctorOptions,
}) {
  // One track per RENDERED day, which is the anchor week plus however many
  // neighbouring days the current column width leaves room for — so the count is
  // dynamic and can't be a static Tailwind class. Every grid layer reads the same
  // template off a CSS var, applied only from `sm` up (below that the event list
  // stays a single stacked column, as it always has).
  const columnCount = weekDays.length;
  const columnTemplateStyle = {
    "--week-cols": `repeat(${columnCount}, minmax(0, 1fr))`,
  };
  const anchorStartIndex = dayWindow?.anchorStartIndex ?? 0;
  const anchorEndIndex = anchorStartIndex + (dayWindow?.anchorLength ?? columnCount);
  const isPaddingIndex = (index) => index < anchorStartIndex || index >= anchorEndIndex;
  const doctorNameById = useMemo(() => {
    const map = new Map();
    if (Array.isArray(doctorOptions)) {
      for (const opt of doctorOptions) {
        const id = String(opt?.id || "").trim();
        const name = String(opt?.name || "").trim();
        if (id && name) map.set(id, name);
      }
    }
    return map;
  }, [doctorOptions]);
  const blocksByDayKey = useMemo(() => {
    const map = new Map();
    if (Array.isArray(weekDays)) {
      for (const day of weekDays) {
        map.set(toDateKey(day), blocksForDate(timeOffBlocks, day));
      }
    }
    return map;
  }, [weekDays, timeOffBlocks]);
  // Sorted events + overlap-lane layout per day, hoisted out of the render
  // loop so drag-preview re-renders (each 5-min step) don't re-sort and
  // re-layout all seven days.
  const dayLayoutsByKey = useMemo(() => {
    const map = new Map();
    if (Array.isArray(weekDays)) {
      for (const day of weekDays) {
        const dayKey = toDateKey(day);
        const dayEvents = [...(eventsByDate[dayKey] || [])].sort(
          (left, right) => left.startAt.toMillis() - right.startAt.toMillis(),
        );
        map.set(dayKey, { dayEvents, dayLayout: computeEventColumnLayout(dayEvents) });
      }
    }
    return map;
  }, [weekDays, eventsByDate]);
  const hasAnyBlocks = useMemo(() => {
    for (const arr of blocksByDayKey.values()) {
      if (arr.length > 0) return true;
    }
    return false;
  }, [blocksByDayKey]);
  const nowAnchorRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const previousZoomPercentRef = useRef(zoomPercent);
  const previousWidthZoomPercentRef = useRef(widthZoomPercent);
  const centerScrollRatioRef = useRef(null);
  // The grid is never narrower than its pane: below 100% the surplus is filled
  // with neighbouring days rather than left blank, so the width zoom only ever
  // pushes content PAST the viewport. Zoom feeds ONLY these derived numbers —
  // the columns themselves are fixed — so a zoom step is a style change, not a
  // relayout of the day set (and never a refetch).
  // The gutters are measured, not assumed, so a non-16px root font still frames.
  const gutterPx = WEEK_GUTTER_REM * getRootFontSizePx();
  const metrics = useMemo(
    () => weekColumnMetrics(dayWindow, widthZoomPercent),
    [dayWindow, widthZoomPercent],
  );
  const contentWidthStyle = weekGridWidthStyle(metrics, gutterPx) || undefined;
  const scrollOrientation = contentWidthStyle ? "both" : "vertical";
  const [currentTime, setCurrentTime] = useState(() => DateTime.now());
  const currentMinutes = useMemo(() => currentTime.hour * 60 + currentTime.minute, [currentTime]);
  // Null when now is outside the visible window — the indicator is then omitted.
  const nowIndicator = useMemo(
    () => nowIndicatorForAxis(currentMinutes, timeAxis),
    [currentMinutes, timeAxis],
  );

  useEffect(() => {
    // Re-seed on every effect run: the state initializer only runs on first
    // mount, so an Activity thaw would otherwise show the freeze-time line
    // for up to a minute.
    setCurrentTime(DateTime.now());
    const timerId = window.setInterval(() => {
      setCurrentTime(DateTime.now());
    }, 60000);

    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      return;
    }

    const scrollToPosition = () => {
      // An Activity re-show re-runs this "mount-only" effect, but the browser kept the
      // viewport's exact offsets through display:none — re-deriving a position from the
      // now anchor or the saved ratio would move the view away from where the user left it.
      if (viewport.scrollTop > 0 || viewport.scrollLeft > 0) {
        onDidInitialScroll?.();
        return;
      }
      if (initialScrollToNow) {
        nowAnchorRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
        onDidInitialScroll?.();
        return;
      }
      if (initialScrollRatio != null && initialScrollRatio >= 0 && initialScrollRatio <= 1) {
        const scrollHeight = Math.max(viewport.scrollHeight, 1);
        const centerPx = initialScrollRatio * scrollHeight;
        const scrollTop = centerPx - viewport.clientHeight / 2;
        const maxScrollTop = Math.max(viewport.scrollHeight - viewport.clientHeight, 0);
        viewport.scrollTop = Math.min(Math.max(scrollTop, 0), maxScrollTop);
      }
      onDidInitialScroll?.();
    };

    window.requestAnimationFrame(scrollToPosition);
    // Run only on mount; initialScrollToNow/initialScrollRatio are from first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only initial scroll
  }, []);

  useEffect(() => {
    const scrollAreaViewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!scrollAreaViewport) {
      return;
    }

    const updateCenterScrollRatio = () => {
      // A hidden viewport (Activity display:none — including this effect's own cleanup, which
      // runs AFTER the hide commit) measures 0x0 with scrollTop reset; saving that would
      // overwrite the real position with ~0 and every thaw would restore to the top.
      if (scrollAreaViewport.clientHeight === 0) return;
      const scrollHeight = Math.max(scrollAreaViewport.scrollHeight, 1);
      const centerPx = scrollAreaViewport.scrollTop + scrollAreaViewport.clientHeight / 2;
      const ratio = Math.min(Math.max(centerPx / scrollHeight, 0), 1);
      centerScrollRatioRef.current = ratio;
      onSaveScrollRatio?.(ratio);
    };

    updateCenterScrollRatio();
    scrollAreaViewport.addEventListener("scroll", updateCenterScrollRatio, { passive: true });
    return () => {
      scrollAreaViewport.removeEventListener("scroll", updateCenterScrollRatio);
      updateCenterScrollRatio();
    };
  }, [onSaveScrollRatio]);

  useEffect(() => {
    if (previousZoomPercentRef.current === zoomPercent) {
      return;
    }

    const scrollAreaViewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!scrollAreaViewport) {
      previousZoomPercentRef.current = zoomPercent;
      return;
    }

    // Cursor-anchored (wheel) zoom: put the point under the cursor back under the
    // cursor, measured off real rects rather than a rem-derived px-per-minute —
    // the fine rows stretch past their rem minimum, so the formula drifted (see
    // calendar-zoom-anchor.js).
    const anchorObj = zoomAnchorRef?.current;
    const anchor =
      anchorObj && anchorObj.view === "week" && anchorObj.axes?.vertical ? anchorObj : null;
    if (anchor?.measured) {
      const measured = anchor.measured;
      anchor.axes.vertical = false;
      if (!anchor.axes.horizontal) {
        zoomAnchorRef.current = null;
      }
      window.requestAnimationFrame(() => {
        applyZoomAnchor(scrollAreaViewport, measured, { vertical: true });
        const scrollHeight = Math.max(scrollAreaViewport.scrollHeight, 1);
        centerScrollRatioRef.current = Math.min(
          Math.max((scrollAreaViewport.scrollTop + scrollAreaViewport.clientHeight / 2) / scrollHeight, 0),
          1,
        );
      });
      previousZoomPercentRef.current = zoomPercent;
      return;
    }
    if (anchor) {
      // Anchor present but the grid could not be measured (hidden pane): give up
      // the axis cleanly and fall through to the centre-ratio path below.
      anchor.axes.vertical = false;
      if (!anchor.axes.horizontal) {
        zoomAnchorRef.current = null;
      }
    }

    const fallbackCenterScrollRatio = centerScrollRatioRef.current;

    window.requestAnimationFrame(() => {
      const scrollHeight = Math.max(scrollAreaViewport.scrollHeight, 1);
      const centerScrollRatio =
        fallbackCenterScrollRatio ??
        Math.min(
          Math.max((scrollAreaViewport.scrollTop + scrollAreaViewport.clientHeight / 2) / scrollHeight, 0),
          1,
        );
      const nextCenterPx = centerScrollRatio * scrollHeight;
      const nextScrollTop = nextCenterPx - scrollAreaViewport.clientHeight / 2;
      const maxScrollTop = Math.max(scrollAreaViewport.scrollHeight - scrollAreaViewport.clientHeight, 0);
      scrollAreaViewport.scrollTop = Math.min(Math.max(nextScrollTop, 0), maxScrollTop);
      centerScrollRatioRef.current = Math.min(
        Math.max((scrollAreaViewport.scrollTop + scrollAreaViewport.clientHeight / 2) / scrollHeight, 0),
        1,
      );
    });

    previousZoomPercentRef.current = zoomPercent;
  }, [zoomPercent]);

  // Horizontal framing. The rendered run STARTS a week earlier, so scrollLeft 0
  // would open on last week — the viewport is instead parked so the anchor week
  // is framed. Keyed on the WEEK only: zooming must not yank the view back here
  // (the compensation effect below holds the user's position instead).
  const anchorWeekKey = weekDays[dayWindow?.anchorStartIndex ?? 0]?.toISODate?.() || "";
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      return undefined;
    }
    let frameId = 0;
    let attempts = 0;
    const frameAnchorWeek = () => {
      // A frozen workspace measures 0 wide; retry briefly rather than framing
      // against a zero-width viewport (which would land everything at 0).
      if (viewport.clientWidth === 0 && attempts < 30) {
        attempts += 1;
        frameId = window.requestAnimationFrame(frameAnchorWeek);
        return;
      }
      const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
      const target = weekAnchorScrollLeft(dayWindow, metrics, viewport.scrollWidth, gutterPx);
      viewport.scrollLeft = Math.min(target, maxScrollLeft);
    };
    // Double rAF: the column template and min-width land in the same commit, so
    // scrollWidth is only trustworthy a frame after the relayout.
    frameId = window.requestAnimationFrame(() => {
      frameId = window.requestAnimationFrame(frameAnchorWeek);
    });
    return () => window.cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-frame on a new week only
  }, [anchorWeekKey]);

  // Horizontal ("width") zoom compensation: keep the column under the cursor (or
  // the viewport centre) anchored as the columns widen/narrow. Ratio-based on
  // scrollWidth, which is sound precisely because the day run is FIXED — the
  // same content is on screen before and after, only wider or narrower.
  useEffect(() => {
    if (previousWidthZoomPercentRef.current === widthZoomPercent) {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      previousWidthZoomPercentRef.current = widthZoomPercent;
      return undefined;
    }
    const anchorObj = zoomAnchorRef?.current;
    const useCursor = !!(anchorObj && anchorObj.view === "week" && anchorObj.axes?.horizontal);
    const measured = useCursor ? anchorObj.measured : null;
    // Centre fallback (Display-popover zoom, or an unmeasurable grid): hold the
    // middle column, captured the same grid-relative way as a cursor anchor.
    const fallback = measured ? null : centreZoomAnchor(viewport);
    // Shared zoom-anchor protocol: release our axis so the vertical effect can
    // null the anchor once both axes are done (see use-calendar-zoom-pan.js).
    if (anchorObj && anchorObj.view === "week" && anchorObj.axes) {
      anchorObj.axes.horizontal = false;
      if (!anchorObj.axes.vertical) {
        zoomAnchorRef.current = null;
      }
    }
    const id = window.requestAnimationFrame(() => {
      applyZoomAnchor(viewport, measured || fallback, { horizontal: true });
    });
    previousWidthZoomPercentRef.current = widthZoomPercent;
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- zoomAnchorRef is a ref box
  }, [widthZoomPercent]);

  // Register this view's scroll viewport for the shared zoom/pan hook.
  useEffect(() => {
    if (typeof registerViewport !== "function") {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    registerViewport(viewport || null);
    return () => registerViewport(null);
  }, [registerViewport]);

  // Quick-navigation reset: re-scroll to "now" after the 100%-zoom relayout (double-rAF so it
  // wins over the zoom-change effect's own rAF).
  useEffect(() => {
    if (!resetSignal) {
      return undefined;
    }
    const viewport = scrollAreaRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!viewport) {
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        // Back to the anchor week, not to column 0 — column 0 is a padding day.
        const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
        const target = weekAnchorScrollLeft(dayWindow, metrics, viewport.scrollWidth, gutterPx);
        viewport.scrollLeft = Math.min(target, maxScrollLeft);
        nowAnchorRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
      });
    });
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset is signal-driven
  }, [resetSignal]);

  return (
    <div className="isolate flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <ScrollArea
        ref={scrollAreaRef}
        orientation={scrollOrientation}
        className="min-h-0 flex-1"
      >
        <div className="flex min-h-full flex-col" style={contentWidthStyle}>
          {/* Day-name + all-day header. Sticky-top so it pins during vertical scroll, and
              inside the scroller so it tracks the columns during horizontal (width) zoom. */}
          <div className="sticky top-0 z-30 bg-background shadow-sm ring-1 ring-border/60 sm:pr-8">
          <div
            style={columnTemplateStyle}
            className="-mr-px hidden grid-cols-(--week-cols) divide-x divide-border/60 border-r border-border text-sm/6 text-muted-foreground sm:grid"
          >
            <div className="sticky left-0 z-10 col-end-1 w-14 bg-background" />
            {weekDays.map((day, index) => (
              <button
                key={toDateKey(day)}
                type="button"
                onClick={() => onSelectDate(day)}
                title={day.toFormat("cccc, d LLLL yyyy")}
                // Padding days are context, not the week you asked for.
                className={cn(
                  "flex items-center justify-center py-3",
                  isPaddingIndex(index) && "opacity-55",
                )}
              >
                <span className={cn("flex items-baseline", isToday(day) && "text-primary")}>
                  {/* The run spills across month boundaries, so the first of a
                      month names its month instead of its weekday. */}
                  {day.day === 1 ? day.toFormat("LLL") : day.toFormat("ccc")}{" "}
                  <span
                    className={cn(
                      "ml-1.5 flex size-8 items-center justify-center rounded-full font-semibold text-foreground",
                      isSameDay(day, selectedDate) && "bg-foreground text-background",
                      !isSameDay(day, selectedDate) && isToday(day) && "bg-primary text-primary-foreground",
                    )}
                  >
                    {day.day}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {hasAnyBlocks ? (
            <div
              style={columnTemplateStyle}
              className="-mr-px hidden grid-cols-(--week-cols) divide-x divide-border/40 border-y border-border/60 bg-muted/15 text-xs sm:grid"
            >
              <div className="sticky left-0 z-10 col-end-1 w-14 bg-background px-1 py-1 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                All day
              </div>
              {weekDays.map((day) => {
                const dayKey = toDateKey(day);
                const dayBlocks = blocksByDayKey.get(dayKey) || [];
                return (
                  <div
                    key={dayKey}
                    className="flex min-h-[1.75rem] flex-wrap items-center gap-1 px-1.5 py-1"
                  >
                    {dayBlocks.map((block) => (
                      <BlockChip
                        key={block.id}
                        block={block}
                        doctorName={
                          block.scope !== "global" && block.ownerId
                            ? doctorNameById.get(block.ownerId) || null
                            : null
                        }
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ) : null}
          </div>

          <div className="flex flex-auto">
            <div className="sticky left-0 z-10 w-14 flex-none bg-background ring-1 ring-border/60" />
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              <div
                style={{ gridTemplateRows: timeAxis.tickRowTemplate }}
                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-border/60"
              >
                <div className="row-end-1 h-7" />
                {timeAxis.ticks.map((tick) => (
                  <div key={tick.key} />
                ))}
              </div>

              {/* Time labels ride the FINE lattice, not the tick rows, so their
                  density follows the zoom rather than the gridline interval (see
                  getCalendarTimeAxis). Each one is pulled out of the grid with
                  -ml-14 so it renders inside the sticky gutter to the left. */}
              <div
                style={{ gridTemplateRows: timeAxis.fineRowTemplate }}
                className="pointer-events-none col-start-1 col-end-2 row-start-1 grid"
              >
                {timeAxis.labels.map((label) => (
                  <div
                    key={label.key}
                    style={{ gridRow: `${label.row} / span 1` }}
                    className={cn(
                      "sticky left-0 z-20 -mt-2.5 -ml-14 w-14 whitespace-nowrap pr-2 text-right",
                      label.isHour
                        ? "text-xs/5 text-muted-foreground"
                        : "text-[10px]/5 text-muted-foreground/60",
                    )}
                  >
                    {label.isHour
                      ? formatHourLabel(label.hour, { hour12: use12HourFormat })
                      : formatSubHourLabel(label.startMinute, { hour12: use12HourFormat })}
                  </div>
                ))}
              </div>

              <div
                style={columnTemplateStyle}
                className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-border/60 sm:grid sm:grid-cols-(--week-cols)"
              >
                {weekDays.map((day, index) => {
                  const dayKey = toDateKey(day);
                  // Only public holidays paint the column overlay. Per-doctor blocks
                  // (vacation, ooo, etc.) live only in the all-day banner row above.
                  const holidayBlocks = (blocksByDayKey.get(dayKey) || []).filter(
                    (b) => b.type === "holiday",
                  );
                  return (
                    <WeekDayColumnDroppable
                      key={dayKey}
                      dayKey={dayKey}
                      isPadding={isPaddingIndex(index)}
                      className="relative row-span-full"
                    >
                      {holidayBlocks.length > 0 ? <BlockOverlay blocks={holidayBlocks} /> : null}
                    </WeekDayColumnDroppable>
                  );
                })}
                <div className="row-span-full w-8" />
              </div>

              <ol
                // Vertical geometry source for resize + drag grab-offset. The
                // per-day `data-time-col` droppables sit in a sibling background
                // layer, so a card can't reach one with `closest()`; this grid
                // shares their exact top/height (same single grid cell).
                data-time-grid=""
                style={{ ...columnTemplateStyle, gridTemplateRows: timeAxis.fineRowTemplate }}
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-(--week-cols) sm:pr-8"
              >
                {nowIndicator ? (
                  <li
                    style={{ gridRow: `${nowIndicator.rowStart} / span 1` }}
                    className="pointer-events-none relative z-30 col-start-1 col-end-2 sm:col-span-full"
                  >
                    <div
                      ref={nowAnchorRef}
                      style={{ top: `${nowIndicator.rowOffsetPercent}%` }}
                      className="absolute left-0 right-0 border-t-2 border-primary/90"
                    />
                    <span
                      style={{ top: `${nowIndicator.rowOffsetPercent}%` }}
                      className="absolute -left-1 size-3 -translate-y-1/2 rounded-full border border-background bg-primary shadow-sm"
                    />
                  </li>
                ) : null}

                {dragPlaceholder
                  ? (() => {
                      // Snapped landing preview for an in-flight appointment drag.
                      const dayIndex = weekDays.findIndex(
                        (day) => toDateKey(day) === dragPlaceholder.dayKey,
                      );
                      if (dayIndex < 0) return null;
                      return (
                        <li
                          style={{
                            gridRow: `${dragPlaceholder.startRow} / span ${dragPlaceholder.span}`,
                            "--col": String(dayIndex + 1),
                          }}
                          className="pointer-events-none relative z-40 mt-px flex sm:col-start-(--col)"
                        >
                          {/* Tinted to the dragged card's palette so the landing spot
                              reads as "this appointment goes here". Border tint rides
                              on the local `--border` var — the unlayered
                              `* { @apply border-border }` in app.css outranks
                              border-color utilities but reads from that var. */}
                          <div
                            style={dragPlaceholder.theme || undefined}
                            className={cn(
                              "absolute inset-x-[3px] top-0.5 bottom-1 rounded-xl border-2 border-dashed",
                              dragPlaceholder.theme
                                ? "[--border:color-mix(in_oklab,var(--event-text-light)_60%,transparent)] dark:[--border:color-mix(in_oklab,var(--event-text-dark)_60%,transparent)] bg-(--event-bg-light)/80 dark:bg-(--event-bg-dark)/80"
                                : dragPlaceholder.newPatient
                                  ? "[--border:color-mix(in_oklab,var(--color-violet-500)_70%,transparent)] bg-violet-500/10"
                                  : "[--border:color-mix(in_oklab,var(--color-emerald-500)_70%,transparent)] bg-emerald-500/10",
                            )}
                          />
                        </li>
                      );
                    })()
                  : null}

                {weekDays.map((day, index) => {
                  const { dayEvents, dayLayout } = dayLayoutsByKey.get(toDateKey(day)) || {
                    dayEvents: [],
                    dayLayout: {},
                  };
                  return dayEvents.map((event) => {
                    const position = eventGridPositionForAxis(event, timeAxis);
                    const layout = dayLayout[event.id] || { columnIndex: 0, totalColumns: 1 };
                    const leftPercent = (layout.columnIndex / layout.totalColumns) * 100;
                    const widthPercent = 100 / layout.totalColumns;
                    return (
                      <li
                        key={event.id}
                        data-span-minutes={fitSpanMinutes(event, position) ?? undefined}
                        style={{ gridRow: `${position.startRow} / span ${position.span}`, "--col": String(index + 1) }}
                        className="relative mt-px flex sm:col-start-(--col)"
                      >
                        <CalendarEventItem
                          event={event}
                          use12HourFormat={use12HourFormat}
                          variant="compact"
                          as="div"
                          timeAxis={timeAxis}
                          style={{
                            left: `calc(${leftPercent}% + 3px)`,
                            width: `calc(${widthPercent}% - 6px)`,
                            zIndex: 20 + layout.columnIndex,
                          }}
                        />
                      </li>
                    );
                  });
                })}
              </ol>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default WeekView;
